import { reset } from "redux-form";
import { call, fork, put, take } from "redux-saga/effects";
import { NotificationCustomer } from "../../config/view_const/notification";
import { AppError, ErrorType } from "../../domain/model/app_error";
import { AppNotificationType } from "../../domain/model/app_notification_message";
import { CustomerBlackList } from "../../domain/model/customer_black_list";
import { ICustomerBlackListRepository } from "../../domain/repository/customer_black_list_repository";
import { createApiClient, IApiClient } from "../../infra/api/client";
import { createCustomerBlackListAPI } from "../../infra/api/service/customer_black_list_api";
import {
  createAppNotificationMessageActionCreator,
  IAppNotificationMessageActionCreator,
} from "../action/app_notification_message_action";
import {
  createCustomerBlackListActionCreator,
  CustomerBlackListActionType,
  ICustomerBlackListActionCreator,
  IRequestAddCustomerBlackListAction,
  IRequestDeleteCustomerBlackListAction,
  IRequestGetCustomerBlackListAction,
} from "../action/customer_black_list_action";
import { AddAppNotificationRequest } from "../request/app_notification_message_request";
import {
  AddCustomerBlackListResponse,
  DeleteCustomerBlackListResponse,
  GetCustomerBlackListResponse,
} from "../response/customer_black_list_response";
import { handleErrorForHandler } from "./handleErrorForHandler";

const apiClient: IApiClient = createApiClient();
const customerBlackListRepository: ICustomerBlackListRepository =
  createCustomerBlackListAPI(apiClient);
const actionCreator: ICustomerBlackListActionCreator =
  createCustomerBlackListActionCreator();
const actionCreatorForNotification: IAppNotificationMessageActionCreator =
  createAppNotificationMessageActionCreator();

function* handleGetCustomerBlackListInCustomerBlackList() {
  while (true) {
    try {
      const action: IRequestGetCustomerBlackListAction = yield take(
        CustomerBlackListActionType.REQUEST_GET_CUSTOMER_BLACK_LIST
      );

      let customerBlackList: CustomerBlackList | null = null;
      try {
        customerBlackList = yield call(
          getCustomerBlackList,
          action.item.customerId
        );
      } catch (error) {
        const e = error as AppError;
        if (!(e.type === ErrorType.API_REQUEST_404)) {
          throw error;
        }
      }

      const res: GetCustomerBlackListResponse =
        new GetCustomerBlackListResponse();
      res.customerBlackList = customerBlackList;
      yield put(actionCreator.callbackGetCustomerBlackListAction(true, res));
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(actionCreator.callbackGetCustomerBlackListAction(false));
    }
  }
}

function* handleAddCustomerBlackListInCustomerBlackList() {
  while (true) {
    try {
      const action: IRequestAddCustomerBlackListAction = yield take(
        CustomerBlackListActionType.REQUEST_ADD_CUSTOMER_BLACK_LIST
      );
      const domains: string[] = action.item.domains;
      const newDomains: string[] = domains.concat(action.item.domain);

      yield call(updateCustomerBlackList, newDomains);

      let customerBlackList: CustomerBlackList | null = null;
      try {
        customerBlackList = yield call(
          getCustomerBlackList,
          action.item.customerId
        );
      } catch (error) {
        const e = error as AppError;
        if (!(e.type === ErrorType.API_REQUEST_404)) {
          throw error;
        }
      }

      const res: AddCustomerBlackListResponse =
        new AddCustomerBlackListResponse();
      res.customerBlackList = customerBlackList;
      yield put(reset("CustomerBlackListAddForm"));
      yield put(actionCreator.callbackAddCustomerBlackListAction(true, res));

      const notificationReq: AddAppNotificationRequest =
        new AddAppNotificationRequest();
      notificationReq.type = AppNotificationType.INFO;
      notificationReq.message = NotificationCustomer.successAddBlackList;
      yield put(actionCreatorForNotification.requestAddAction(notificationReq));
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(actionCreator.callbackAddCustomerBlackListAction(false));
    }
  }
}

function* handleDeleteCustomerBlackListInCustomerBlackList() {
  while (true) {
    try {
      const action: IRequestDeleteCustomerBlackListAction = yield take(
        CustomerBlackListActionType.REQUEST_DELETE_CUSTOMER_BLACK_LIST
      );
      const domains: string[] = action.item.domains;
      const newDomains = domains.filter(
        (value: string, index: number) => index !== action.item.index
      );

      yield call(updateCustomerBlackList, newDomains);

      let customerBlackList: CustomerBlackList | null = null;
      try {
        customerBlackList = yield call(
          getCustomerBlackList,
          action.item.customerId
        );
      } catch (error) {
        const e = error as AppError;
        if (!(e.type === ErrorType.API_REQUEST_404)) {
          throw error;
        }
      }

      const res: DeleteCustomerBlackListResponse =
        new DeleteCustomerBlackListResponse();
      res.customerBlackList = customerBlackList;
      yield put(actionCreator.callbackDeleteCustomerBlackListAction(true, res));

      const notificationReq: AddAppNotificationRequest =
        new AddAppNotificationRequest();
      notificationReq.type = AppNotificationType.INFO;
      notificationReq.message = NotificationCustomer.successDeleteBlackList;
      yield put(actionCreatorForNotification.requestAddAction(notificationReq));
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(actionCreator.callbackDeleteCustomerBlackListAction(false));
    }
  }
}

const getCustomerBlackList = (
  customerId: string
): Promise<CustomerBlackList> => {
  return customerBlackListRepository.get(customerId);
};

const updateCustomerBlackList = (domains: string[]): Promise<void> => {
  return customerBlackListRepository.update(domains);
};

export {
  handleGetCustomerBlackListInCustomerBlackList,
  handleAddCustomerBlackListInCustomerBlackList,
  handleDeleteCustomerBlackListInCustomerBlackList,
};



// WEBPACK FOOTER //
// ./src/presentation/handler/customer_black_list_handler.ts